import { Match } from 'effect';
import type { ReturnsRangeInfo, SelectedProxy } from './utils';
import { getDisabledProxyTypeMessage, resolveDisabledProxyReasonMessage } from './utils';
import type { FundToProxy } from 'venn-components';
import {
  getBulkCustomProxyOptions,
  getCustomProxyOptionForFundWithExistingProxy,
  type ProxyErrorEnum,
  type ProxyMeta,
  type ProxyOption,
  type ProxyTypeEnum,
} from 'venn-api';
import { getCustomProxyOptionForFund } from 'venn-api';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import {
  IS_PROD,
  createProxyFeasibilityKey,
  isRequestSuccessful,
  logExceptionIntoSentry,
  createBulkProxyFeasibilityKey,
  assertExhaustive,
  getExtrapolateFromValue,
  validateLayeredProxyType,
} from 'venn-utils';
import { useCallback } from 'react';
import { useProxyTypeValidation } from './useProxyTypeValidation';
import type { FundToBulkProxy } from '../types';
import {
  LayeredProxyConfiguration as LayeredProxyConfigurationEnum,
  type LayeredProxyConfiguration,
} from './proxy-types';

type ProxyErrorProps = {
  investment: FundToProxy;
  selectedProxyId?: string;
  selectedProxyType?: ProxyTypeEnum;
  numLags?: number;
  extrapolate?: boolean;
  rawInvestmentReturnsRange: ReturnsRangeInfo | null;
  rawProxyReturnsRange: ReturnsRangeInfo | null;
};

export const getInvalidProxyMessage = (reason: ProxyErrorEnum | undefined): string | undefined => {
  if (!reason) {
    return undefined;
  }
  switch (reason) {
    case 'INVALID_FREQUENCY':
      return 'Unable to apply this proxy. Proxy frequency is invalid.';
    case 'BELOW_THRESHOLD':
      return 'Unable to apply this proxy. The proxy sits outside our recommended threshold for this investment.';
    case 'NO_OVERLAP':
      return 'Unable to apply this proxy. Your investment and the chosen proxy do not have any overlapping period.';
    case 'INVESTMENT_SERIES_TOO_SHORT':
      return 'Unable to proxy. Your investment does not have enough datapoints.';
    case 'PROXY_SERIES_TOO_SHORT':
      return 'Unable to apply this proxy. The proxy does not have enough datapoints.';
    case 'NON_RETURNS_SERIES':
      return 'Unable to apply this proxy. Proxying is only allowed for returns timeseries';
    case 'UNABLE_TO_DESMOOTH_NOT_ENOUGH_OVERLAP':
      return 'Unable to desmooth. There is not enough overlap between your investment and the chosen proxy.';
    case 'UNABLE_TO_DESMOOTH_INVALID_N_LAGS':
      return 'Unable to desmooth. Invalid number of lags.';
    case 'UNABLE_TO_EXTRAPOLATE_SERIES_TOO_SHORT':
      return (
        'This investment does not have enough history to enable extrapolation. ' +
        'Choose an investment with a longer history of returns data, or continue without extrapolation.'
      );
    case 'UNABLE_TO_EXTRAPOLATE_NOT_ENOUGH_OVERLAP':
      return (
        'This proxy does not have sufficient overlapping history to extrapolate the returns of the investment. ' +
        'Choose a proxy with a longer history of returns data, or continue without extrapolation.'
      );
    case 'UNABLE_TO_EXTRAPOLATE_PROXY_NOT_EXTENDING':
      return (
        'This proxy does not extend the returns of your investment. ' +
        'Choose a proxy with a more recent history of returns data, or continue without extrapolation.'
      );
    case 'UNABLE_TO_EXTRAPOLATE':
      return (
        'Unable to extrapolate. ' +
        'Choose a proxy with a longer history of returns data, or continue without extrapolation.'
      );
    case 'UNABLE_TO_INTERPOLATE_NOT_ENOUGH_OVERLAP':
      return 'Unable to interpolate. There is not enough overlap between your investment and the chosen proxy.';
    case 'UNABLE_TO_PROXY':
      return 'Unable to apply proxy.';
    default:
      throw assertExhaustive(reason);
  }
};

type ErrorInfo = {
  /** Reason for disabling the proxy */
  errorMessage: string | undefined;
  /** Whether current state is invalid and unable to be saved */
  disableSave: boolean;
};

/** Custom hook for error-related logic for proxies. */
const useProxyErrorMessage = ({
  investment,
  selectedProxyId,
  selectedProxyType,
  numLags,
  extrapolate,
  rawInvestmentReturnsRange,
  rawProxyReturnsRange,
}: ProxyErrorProps): ErrorInfo => {
  /** A check for proxy validity performed by the FE only. This is a quick, simplified check that doesn't catch
   * all reasons why the proxy could be invalid, but good enough in most cases */
  const disabledProxyReason = selectedProxyType
    ? getDisabledProxyTypeMessage(selectedProxyType, investment, rawInvestmentReturnsRange, rawProxyReturnsRange)
    : undefined;
  /** A "perfect" check for proxy validity fetched from the BE trying to do the proxy operation itself.
   *  The downside is that it's slower to fetch, but if this check passes then it's guaranteed that proxy is valid. */
  const { data: proxyFeasibilityInfo, status } = useQuery<ProxyOption | null>(
    createProxyFeasibilityKey(investment.id, selectedProxyId, selectedProxyType, numLags, extrapolate),
    async () => {
      if (!selectedProxyId || !selectedProxyType) {
        return null;
      }
      const result = await getCustomProxyOptionForFund(
        investment.id,
        selectedProxyId,
        selectedProxyType,
        numLags,
        extrapolate,
      );
      if (isRequestSuccessful(result)) {
        return result.content;
      }
      logExceptionIntoSentry(
        `Fetching proxy info failed for investment ${investment.id} and potential proxy ${selectedProxyId}.`,
      );
      return null;
    },
  );
  if (!selectedProxyId || !investment) {
    return { errorMessage: 'Please specify a proxy to enable save.', disableSave: true };
  }
  if (disabledProxyReason !== undefined) {
    return {
      errorMessage: disabledProxyReason,
      disableSave: true,
    };
  }
  switch (status) {
    case 'loading':
      return {
        errorMessage: undefined,
        disableSave: true,
      };
    case 'success':
      return {
        errorMessage: proxyFeasibilityInfo ? getInvalidProxyMessage(proxyFeasibilityInfo.error) : undefined,
        disableSave: proxyFeasibilityInfo ? !proxyFeasibilityInfo.valid : true,
      };
    default:
      return {
        errorMessage: 'Something went wrong. Please refresh the page or try again later.',
        disableSave: true,
      };
  }
};

export default useProxyErrorMessage;

type LayerInfo = {
  selectedProxy: SelectedProxy | null;
  proxyType: ProxyTypeEnum | undefined;
  extrapolate: boolean;
  numLags: number;
};

type BulkProxyErrorProps = {
  investments: FundToBulkProxy[];
  proxyConfiguration: LayeredProxyConfiguration;
  rawInvestmentReturnsRanges: (ReturnsRangeInfo | null)[] | undefined;
  effectiveSecondLayerRanges: (ReturnsRangeInfo | null)[] | undefined;
  firstLayerProxyReturnsRange: ReturnsRangeInfo | undefined | null;
  secondLayerProxyReturnsRange: ReturnsRangeInfo | undefined | null;
};

function extractLayersFromConfiguration(config: LayeredProxyConfiguration): {
  firstLayer: LayerInfo | null;
  secondLayer: LayerInfo | null;
} {
  return Match.value(config).pipe(
    Match.tag('Empty', () => ({
      firstLayer: null,
      secondLayer: null,
    })),
    Match.tag('SingleLayerable', ({ proxy }) => ({
      firstLayer: {
        selectedProxy: proxy.selectedProxy,
        proxyType: proxy.proxyType,
        extrapolate: getExtrapolateFromValue(proxy),
        numLags: proxy.numLags,
      },
      secondLayer: null,
    })),
    Match.tag('SingleNonLayerable', ({ proxy }) => ({
      firstLayer: {
        selectedProxy: proxy.selectedProxy,
        proxyType: proxy.proxyType,
        extrapolate: getExtrapolateFromValue(proxy),
        numLags: proxy.numLags,
      },
      secondLayer: null,
    })),
    Match.tag('Multi', ({ firstLayer, secondLayer }) => ({
      firstLayer: {
        selectedProxy: firstLayer.selectedProxy,
        proxyType: firstLayer.proxyType,
        extrapolate: getExtrapolateFromValue(firstLayer),
        numLags: firstLayer.numLags,
      },
      secondLayer: {
        selectedProxy: secondLayer.selectedProxy,
        proxyType: secondLayer.proxyType,
        extrapolate: getExtrapolateFromValue(secondLayer),
        numLags: secondLayer.numLags,
      },
    })),
    Match.exhaustive,
  );
}

const createProxyMetaForExistingProxy = (
  layer: LayerInfo,
  fundId: string,
  proxySetId: string | undefined,
): ProxyMeta => ({
  extrapolate: layer.extrapolate,
  fundId,
  numLags: layer.numLags,
  proxyId: layer.selectedProxy?.id,
  proxyOrder: 0,
  proxySetId: proxySetId ?? null,
  proxyType: layer.proxyType!,
  proxyVariant: 'RETURN',
  source: 'APP',
});

export type BulkProxyValidationState =
  | {
      state: 'invalidSelection';
      error: string;
    }
  | {
      state: 'loading';
      selectedProxy: SelectedProxy;
    }
  | {
      state: 'ready';
      error?: string;
      investmentInfo: Record<
        string,
        { disableSave: boolean; errorMessage: string | undefined; suggestedLags: number | undefined } | null
      >;
      selectedProxy: SelectedProxy;
    };

const useValidateLayeredProxyCallback = (
  extrapolate: boolean,
  proxyLayer: 'first' | 'second',
): NonNullable<Parameters<typeof useProxyTypeValidation>[3]> => {
  return useCallback(
    ({ investment, proxyType, rawInvestmentRange }) => {
      return validateLayeredProxyType({
        investment: {
          frequency: rawInvestmentRange?.frequency ?? investment.returnFrequency,
          investmentSource: investment.investmentSource,
        },
        proxy: { proxyType, extrapolate, proxyLayer },
      })?.message;
    },
    [extrapolate, proxyLayer],
  );
};

export const useBulkProxyErrorMessage = ({
  investments,
  proxyConfiguration,
  rawInvestmentReturnsRanges,
  effectiveSecondLayerRanges,
  firstLayerProxyReturnsRange,
  secondLayerProxyReturnsRange,
}: BulkProxyErrorProps): BulkProxyValidationState => {
  const proxySetId = useRecoilValue(proxySetIdState);

  const isMulti = LayeredProxyConfigurationEnum.$is('Multi')(proxyConfiguration);

  const { firstLayer, secondLayer } = extractLayersFromConfiguration(proxyConfiguration);

  const validateFirstLayerLayeredProxy = useValidateLayeredProxyCallback(firstLayer?.extrapolate ?? false, 'first');
  const firstLayerFrontendValidation = useProxyTypeValidation(
    investments,
    rawInvestmentReturnsRanges ?? [],
    firstLayerProxyReturnsRange,
    validateFirstLayerLayeredProxy,
  );
  const firstLayerInvestmentIdsWithoutFrontendErrors =
    firstLayer?.proxyType && firstLayer.selectedProxy
      ? investments
          .filter(
            (_investment, index) =>
              !firstLayerFrontendValidation[firstLayer.proxyType!]?.investmentInfo[index]?.disabledMessage,
          )
          .map(({ id }) => id)
      : [];

  const { data: firstLayerBackendData, status: firstLayerStatus } = useQuery({
    staleTime: 5 * 60 * 1000,
    queryKey: createBulkProxyFeasibilityKey(
      firstLayer?.selectedProxy?.id,
      firstLayer?.proxyType,
      firstLayer?.extrapolate ?? false,
      ...firstLayerInvestmentIdsWithoutFrontendErrors,
    ),
    queryFn: async () => {
      const result = await getBulkCustomProxyOptions(
        firstLayer!.selectedProxy!.id,
        firstLayer!.proxyType,
        0,
        firstLayer!.extrapolate,
        proxySetId,
        firstLayerInvestmentIdsWithoutFrontendErrors,
      );

      if (!isRequestSuccessful(result)) {
        logExceptionIntoSentry(
          `Fetching proxy info failed for investments ${firstLayerInvestmentIdsWithoutFrontendErrors}, and potential proxy ${firstLayer?.selectedProxy?.id}.`,
        );
        return null;
      }

      return Object.fromEntries(
        firstLayerInvestmentIdsWithoutFrontendErrors.map((id, idx) => [id, result.content[idx]]),
      );
    },
    enabled:
      !!firstLayer?.selectedProxy &&
      !!rawInvestmentReturnsRanges &&
      !!firstLayerProxyReturnsRange &&
      !!firstLayer?.proxyType,
  });

  const validateSecondLayerLayeredProxy = useValidateLayeredProxyCallback(secondLayer?.extrapolate ?? false, 'second');
  const secondLayerFrontendValidation = useProxyTypeValidation(
    investments,
    effectiveSecondLayerRanges ?? rawInvestmentReturnsRanges ?? [],
    secondLayerProxyReturnsRange,
    validateSecondLayerLayeredProxy,
  );

  const secondLayerInvestmentIdsWithoutFrontendErrors =
    secondLayer?.proxyType && secondLayer.selectedProxy
      ? investments
          .filter(
            (_investment, index) =>
              !secondLayerFrontendValidation[secondLayer.proxyType!]?.investmentInfo[index]?.disabledMessage,
          )
          .map(({ id }) => id)
      : [];

  const secondLayerQueries = useQueries({
    queries: secondLayerInvestmentIdsWithoutFrontendErrors.map((fundId) => ({
      staleTime: 5 * 60 * 1000,
      queryKey: [
        'layeredProxyFeasibility',
        fundId,
        secondLayer?.selectedProxy?.id,
        secondLayer?.proxyType,
        secondLayer?.extrapolate ?? false,
        firstLayer?.selectedProxy?.id,
        firstLayer?.proxyType,
      ],
      queryFn: async () => {
        const existingProxies = firstLayer ? [createProxyMetaForExistingProxy(firstLayer, fundId, proxySetId)] : [];

        const result = await getCustomProxyOptionForFundWithExistingProxy(
          fundId,
          secondLayer!.selectedProxy!.id,
          secondLayer!.proxyType,
          0,
          secondLayer!.extrapolate,
          existingProxies,
        );

        if (!isRequestSuccessful(result)) {
          logExceptionIntoSentry(
            `Fetching layered proxy info failed for investment ${fundId} and potential proxy ${secondLayer?.selectedProxy?.id}.`,
          );
          return { fundId, data: null };
        }

        return { fundId, data: result.content };
      },
      enabled:
        !!secondLayer?.selectedProxy &&
        !!rawInvestmentReturnsRanges &&
        !!secondLayerProxyReturnsRange &&
        !!secondLayer?.proxyType &&
        !!firstLayer,
    })),
  });

  const secondLayerStatus = secondLayerQueries.some((r) => r.status === 'loading')
    ? 'loading'
    : secondLayerQueries.every((r) => r.status === 'success')
      ? 'success'
      : 'error';

  const secondLayerBackendData: Record<string, ProxyOption | undefined> | null =
    secondLayerStatus === 'success'
      ? Object.fromEntries(
          secondLayerQueries
            .map((r) => r.data)
            .filter((d): d is { fundId: string; data: ProxyOption | null } => d !== undefined)
            .map(({ fundId, data }) => [fundId, data ?? undefined]),
        )
      : null;

  const getLayerValidationResult = (
    layer: LayerInfo | null,
    frontendValidation: ReturnType<typeof useProxyTypeValidation>,
    backendData: Record<string, ProxyOption | undefined> | null | undefined,
    investmentId: string,
    index: number,
  ): { disableSave: boolean; errorMessage: string | undefined; suggestedLags: number | undefined } | null => {
    if (!layer?.proxyType) {
      return null;
    }

    const investmentInfo = frontendValidation[layer.proxyType]?.investmentInfo[index];
    if (investmentInfo?.disabledMessage) {
      return {
        disableSave: true,
        errorMessage: resolveDisabledProxyReasonMessage(investmentInfo.disabledMessage),
        suggestedLags: undefined,
      };
    }

    const proxyInfo = backendData?.[investmentId];
    if (proxyInfo) {
      const errorMessage = getInvalidProxyMessage(proxyInfo.error);
      const disableSave = !proxyInfo.valid;
      const suggestedLags = proxyInfo.suggestedNumLags;

      if (!IS_PROD && disableSave) {
        // eslint-disable-next-line no-console
        console.error(
          `Found backend error for ${investmentId} with proxy type ${layer.proxyType} even though frontend did not find an error.`,
          errorMessage,
          proxyInfo.error,
        );
      }

      return {
        disableSave,
        errorMessage,
        suggestedLags,
      };
    }

    return {
      disableSave: false,
      errorMessage: undefined,
      suggestedLags: undefined,
    };
  };

  const getInfoForInvestment = (
    { id }: FundToBulkProxy,
    index: number,
  ): {
    disableSave: boolean;
    errorMessage: string | undefined;
    suggestedLags: number | undefined;
  } | null => {
    let suggestedLags: number = 0;
    const firstLayerResult = getLayerValidationResult(
      firstLayer,
      firstLayerFrontendValidation,
      firstLayerBackendData,
      id,
      index,
    );
    if (firstLayerResult?.disableSave) {
      return firstLayerResult;
    }
    suggestedLags = firstLayerResult?.suggestedLags ?? 0;

    if (secondLayer) {
      const secondLayerResult = getLayerValidationResult(
        secondLayer,
        secondLayerFrontendValidation,
        secondLayerBackendData,
        id,
        index,
      );
      suggestedLags = secondLayerResult?.suggestedLags ?? 0;
      if (secondLayerResult?.disableSave) {
        return secondLayerResult;
      }
    }

    return {
      disableSave: false,
      errorMessage: undefined,
      suggestedLags,
    };
  };

  const getAllInvestmentInfo = () =>
    Object.fromEntries(
      investments.map((investment, index) => [investment.id, getInfoForInvestment(investment, index)]),
    );

  const investmentErrors = getAllInvestmentInfo();
  const displaySelectedProxy = firstLayer?.selectedProxy ?? null;

  if (!firstLayer?.selectedProxy || !firstLayer?.proxyType) {
    return { state: 'invalidSelection', error: 'Please specify a proxy to enable save.' };
  }
  if (isMulti && (!secondLayer?.selectedProxy || !secondLayer?.proxyType)) {
    return { state: 'invalidSelection', error: 'Please specify a proxy to enable save.' };
  }

  const firstLayerLoading = firstLayerStatus === 'loading';
  const secondLayerLoading = secondLayer && secondLayerStatus === 'loading';

  if (firstLayerLoading || secondLayerLoading) {
    return {
      state: 'loading',
      selectedProxy: displaySelectedProxy!,
    };
  }

  const noValidInvestments = Object.values(investmentErrors).every((error) => error?.disableSave);
  const hasFirstLayerError = firstLayerStatus !== 'success';
  const hasSecondLayerError = secondLayer && secondLayerStatus !== 'success';

  if (hasFirstLayerError || hasSecondLayerError) {
    return {
      state: 'ready',
      error: 'Something went wrong. Please refresh the page or try again later.',
      investmentInfo: investmentErrors,
      selectedProxy: displaySelectedProxy!,
    };
  }

  return {
    state: 'ready',
    error: noValidInvestments ? 'No investments are valid for this proxy' : undefined,
    investmentInfo: investmentErrors,
    selectedProxy: displaySelectedProxy!,
  };
};
